import React, { useContext, useEffect, useMemo, useState, type FormEventHandler } from 'react';
import { withRouter, NextRouter } from 'next/router';
import compose from 'lodash/flowRight';
import { Field } from 'formik';
import { Box, Divider } from '@firsttable/web-components/atoms';
import FormField from '@firsttable/web-components/molecules/FormField/FormField';
import { useLocalStorage } from 'usehooks-ts';
import FormLayout, {
  type FormLayoutProps,
} from '@firsttable/web-components/organisms/Forms/FormLayout';
import { getKeyValuePair } from '@/lib/utils';
import { useAuthState } from '../../context/AuthContext';
import {
  FormContext,
  type FormContextType,
  withFormContext,
} from '../../context/FormContext/FormContext';
import useFacebookLogin from './useFacebookLogin';
import useGoogleLogin from './useGoogleLogin';
import useGoogleAuthCodeLogin from './useGoogleAuthCodeLogin';
import useAppleLogin from './useAppleLogin';
import { NEXT_PUBLIC_LOGIN_METHODS } from '../../../lib/env';
import EmailCheckField from '../Forms/Fields/EmailCheckField';
import SuburbIdsField from '../Forms/Fields/SuburbIdsField';
import withAuth from '../../hocs/withAuth';
import withLocation from '../../hocs/withLocation';
import withBackUrl from '../../hocs/withBackUrl';
import withDynamicFormik from '../../hocs/withDynamicFormik';
import { parseContent } from '../Forms/DynamicForm';
import OAuthWrapper, { type OAuthSignupValues, type OAuthWrapperProps } from './OAuthWrapper';

export type OAuthInitialValues = OAuthSignupValues;

type OAuthProvidersProps = {
  inviteCode?: string;
  cityId?: number;
  children?: React.ReactNode;
  router?: NextRouter;
  actionLabel?: string | null;
  handleSubmit?: FormEventHandler<HTMLFormElement>;
  socialOnly?: boolean;
  expandedSocialButtons?: boolean;
  emailNextButtonKind?: 'cta' | 'normal';
  summariseEmailWhenActive?: boolean;
  emailSummaryClassName?: string;
  signupDefaults?: OAuthInitialValues;
  includeCityId?: boolean;
  includePromoCode?: boolean;
  attributionPromoCode?: string | null;
  // Applied to OAuth completion fields and to the email new-account fields. Sign-in fields are unchanged.
  transformCompletionFields?: OAuthWrapperProps['transformCompletionFields'];
  // Hides matching fields from the rendered form. Validation still uses the stored field list, so required fields also need to be removed in `transformCompletionFields`.
  hiddenFieldNames?: string[];
  redirectURL?: string;
  redirectToMemberCity?: boolean;
  onFlowChange?: (active: boolean, method?: string) => void;
  onComplete?: (auth: { action?: string }) => void;
  actionButtonKind?: FormLayoutProps['actionButtonKind'];
  // From withDynamicFormik, which presents the stored fields for the active experiments.
  formFields?: FormContextType['formFields'];
};

const providers = {
  facebook: useFacebookLogin,
  google_id_token: useGoogleLogin,
  apple: useAppleLogin,
  google: useGoogleAuthCodeLogin,
};

const OAuthProviders = ({
  inviteCode,
  cityId,
  children,
  router,
  actionLabel,
  handleSubmit,
  socialOnly = false,
  expandedSocialButtons = false,
  emailNextButtonKind,
  summariseEmailWhenActive = false,
  emailSummaryClassName,
  signupDefaults,
  includeCityId = true,
  includePromoCode = true,
  attributionPromoCode,
  transformCompletionFields,
  hiddenFieldNames,
  redirectURL,
  redirectToMemberCity = false,
  onFlowChange,
  onComplete,
  actionButtonKind = 'cta',
  formFields,
  ...props
}: OAuthProvidersProps) => {
  const { state, update } = useContext(FormContext);
  const [loginMethods, setLoginMethods] = useState(NEXT_PUBLIC_LOGIN_METHODS);
  const { handleLogin } = useAuthState();
  const [isComplete, setIsComplete] = useState(false);
  const [localStoragePromoCode] = useLocalStorage('firsttable.promoCode', {
    value: '',
    createdAt: '',
  });
  const queryCode = router?.query?.code;
  const queryCity = router?.query?.city;
  const member = state?.member;
  const complete = state?.complete;
  const singleQueryCode = Array.isArray(queryCode) ? queryCode[0] : queryCode;
  const singleQueryCity = Array.isArray(queryCity) ? queryCity[0] : queryCity;
  const queryCityId = singleQueryCity ? Number(singleQueryCity) : undefined;
  const signupValues: OAuthSignupValues = useMemo(
    () => ({
      ...signupDefaults,
      promoCode: includePromoCode
        ? signupDefaults?.promoCode || inviteCode || localStoragePromoCode?.value || singleQueryCode
        : undefined,
      cityId: includeCityId
        ? (signupDefaults?.cityId ??
          cityId ??
          (Number.isFinite(queryCityId) ? queryCityId : undefined))
        : undefined,
    }),
    [
      cityId,
      includeCityId,
      includePromoCode,
      inviteCode,
      localStoragePromoCode?.value,
      queryCityId,
      signupDefaults,
      singleQueryCode,
    ]
  );

  const visibleFormFields = useMemo(
    () => (formFields ?? []).filter((field) => !hiddenFieldNames?.includes(field?.name)),
    [formFields, hiddenFieldNames]
  );

  useEffect(() => {
    if (/FBAN|FBAV|FBMS|FB_IAB|FB4A|FBAN\/Messenger|Instagram/.test(window?.navigator?.userAgent)) {
      setLoginMethods((methods) => methods.filter((method) => !method.includes('google')));
    }
  }, []);

  useEffect(() => {
    if (!router?.isReady) return;
    update({ extraInitialValues: signupValues });
  }, [router?.isReady, signupValues, update]);

  useEffect(() => {
    onFlowChange?.(
      Boolean(state?.method) && state?.member?.accountStatus === 'INCOMPLETE',
      state?.method
    );
  }, [onFlowChange, state?.member?.accountStatus, state?.method]);

  useEffect(() => {
    if (
      member?.accountStatus &&
      member.accountStatus !== 'INCOMPLETE' &&
      complete &&
      complete(member)
    ) {
      setIsComplete(true);
    }
  }, [complete, member]);

  if (!router?.isReady) {
    return null;
  }

  if (isComplete) {
    return <></>;
  }

  const completingAccount = state?.member?.accountStatus === 'INCOMPLETE' && Boolean(state?.method);
  const showDynamicFormFields = completingAccount && visibleFormFields.length > 0;
  // Email sign-up stores an empty list while SignUpFields loads, so that list is not a finished form.
  // Social completion can hide every field once the restaurant supplies them, and still needs a submit.
  const hiddenFieldsAreTheWholeForm =
    !formFields?.length && state?.method !== 'EmailSignUp' && state?.method !== 'EmailSignIn';
  const showCompletionSubmit =
    completingAccount && (showDynamicFormFields || hiddenFieldsAreTheWholeForm);

  return (
    <FormLayout
      {...props}
      {...state}
      handleSubmit={handleSubmit}
      isWide
      actionLabel={showCompletionSubmit ? (actionLabel ?? state?.actionLabel) : null}
      altActions={[]}
      actionButtonKind={actionButtonKind}
      data-social-signup={socialOnly || undefined}>
      {children}
      {!socialOnly &&
        (!state?.method || ['EmailSignIn', 'EmailSignUp'].includes(state.method)) &&
        NEXT_PUBLIC_LOGIN_METHODS.includes('email-universal') && (
          <>
            <Field
              component={FormField}
              id="email-check"
              name="email"
              label={state?.method ? 'Email' : ' '}
              placeholder="Enter email address"
              size="l"
              type={EmailCheckField}
              nextButtonKind={emailNextButtonKind}
              summariseWhenActive={summariseEmailWhenActive}
              summaryClassName={emailSummaryClassName}
              {...props}
              transformSignupFields={transformCompletionFields}
            />
          </>
        )}

      {!socialOnly && !state?.method && <Divider m="28px 0 28px" separatorText="OR" />}

      <div
        className={
          socialOnly || expandedSocialButtons
            ? 'flex w-full flex-col gap-2'
            : 'flex flex-row justify-around gap-2 md:flex-col'
        }>
        {loginMethods.map((method) => {
          const provider = providers[method];
          return (
            provider &&
            (!state?.method || state.method === method) && (
              <OAuthWrapper
                method={method}
                handleLogin={handleLogin}
                useProvider={provider}
                attributionPromoCode={attributionPromoCode}
                transformCompletionFields={transformCompletionFields}
                redirectURL={redirectURL}
                redirectToMemberCity={redirectToMemberCity}
                onComplete={onComplete}
                expanded={socialOnly || expandedSocialButtons}
                // join3's socialOnly stack keeps the lighter label; only the modernised signup matches the bold weight used by every other button in that design.
                boldLabel={expandedSocialButtons}
                key={method}
                {...props}
                signupValues={signupValues}
              />
            )
          );
        })}
      </div>

      {showDynamicFormFields && (
        <Box display="flex" flexDirection="column" gridGap={['15px', '20px']} m="20px 0">
          {visibleFormFields.map((node, index) => {
            if (node.name === 'suburbIds') {
              return (
                <Field
                  {...node}
                  key={index}
                  index={index}
                  component={SuburbIdsField}
                  name={node.name}
                  cityId={state.extraInitialValues?.cityId}
                />
              );
            }
            return (
              <Field
                {...node}
                key={index}
                index={index}
                component={FormField}
                name={node.name}
                id={node.id}
                label={parseContent(
                  ['PLAINTEXT', 'plaintext'].includes(node.type)
                    ? node.value || node.title
                    : (node.title ?? '')
                )}
                value={node.value}
                type={node.type}
                placeholder={node.placeholder}
                options={(node?.options ?? []).filter(Boolean).map((option) => ({
                  inputValue: option?.value,
                  inputLabel: option?.title,
                  inputSelected: option?.selected,
                  inputDisabled: option?.disabled,
                }))}
                data={node.data}
                labelSize={node.labelSize}
                description={parseContent(node.description)}
                className={node.className}
                display={node.display}
                {...getKeyValuePair(node.attributes, 'autoComplete')}
              />
            );
          })}
        </Box>
      )}
    </FormLayout>
  );
};

export default compose(
  withAuth,
  withLocation,
  withBackUrl,
  withRouter,
  withFormContext,
  withDynamicFormik()
)(OAuthProviders);
